import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import Tooltip from './Tooltip';
import { countries, REGION_ORDER, REGION_LABELS } from '../data/countries';
import { CATEGORY_BENCHMARKS, CATEGORY_ORDER } from '../data/categoryBenchmarks';
import { CURRENCY_INFO, POPULAR_DISPLAY_CURRENCIES } from '../data/exchangeRates';

/* Reusable label */
const Label = ({ children }) => (
  <label className="font-sketch font-bold text-label">{children}</label>
);

/* Inline-style toggle pill (works regardless of CSS framework quirks) */
// $/% toggle for the Discount input. Sized to visually match the
// "Discount" label height so the pair reads as one unit, not as a
// label dwarfed by an oversized control.
const pillStyle = (active, withRightBorder = false) => ({
  padding: '0.28rem 0.75rem',
  fontFamily: '"Inter", sans-serif',
  fontWeight: 700,
  fontSize: '1rem',
  lineHeight: 1.1,
  cursor: 'pointer',
  border: 'none',
  ...(withRightBorder ? { borderRight: '1.5px solid rgba(26, 26, 46, 0.12)' } : {}),
  backgroundColor: active ? '#1e3a8a' : '#fefefe',
  color: active ? '#fefefe' : '#1e293b',
  transition: 'all 0.12s',
});

const pillContainerStyle = {
  display: 'inline-flex',
  border: '1.5px solid #1a1a2e',
  borderRadius: '0.5rem',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(26,26,46,0.05)',
};

const baseFields = [
  { name: 'price',        emoji: '💰', label: 'Item Price' },
  { name: 'shipping',     emoji: '📦', label: 'Shipping (You Charge)' },
  { name: 'shippingCost', emoji: '📬', label: 'Shipping (You Pay)' },
  { name: 'cost',         emoji: '🔧', label: 'Materials',
    tip: 'Just the raw materials cost per item. Labor and packaging go below.' },
];

export default function InputsPanel({
  inputs, setInputs,
  settings, setSettings,
  errors,
  onCalculate, onReset,
  ratesStatus, onRefreshRates,
}) {
  const [hidden, setHidden] = useState(false);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({ ...prev, [name]: value }));
  };
  const updS = (patch) => setSettings(s => ({ ...s, ...patch }));

  return (
    <div className="sketch-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-title text-3xl text-primary flex items-center gap-2">
          <CalculatorIcon className="w-7 h-7" />
          Your Numbers
        </h2>
        <button
          onClick={onReset}
          title="Clear all inputs and saved products"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.32rem',
            padding: '0.32rem 0.7rem',
            background: 'rgba(159, 18, 57, 0.08)',
            border: '1.5px solid rgba(159, 18, 57, 0.4)',
            borderRadius: '0.5rem',
            color: '#9f1239',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.86rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.12s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#9f1239';
            e.currentTarget.style.color = '#fefdf8';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(159, 18, 57, 0.08)';
            e.currentTarget.style.color = '#9f1239';
          }}
        >
          <span aria-hidden="true" style={{ fontSize: '0.95rem', lineHeight: 1 }}>↺</span>
          Reset all
        </button>
      </div>

      {/* Settings — single compact row: Country | Currency | Refresh | Offsite */}
      <div className="pb-2 mb-3" style={{ borderBottom: '2px dashed rgba(30,41,59,0.3)' }}>
        <div
          className="grid items-end"
          style={{
            // Currency column sized to just fit a 3-letter code (USD/EUR
            // /GBP) + chevron — frees the rest of the row width for the
            // Country dropdown, which actually benefits from showing
            // more of the country name. The "💱 Currency" label above
            // wraps to two lines when needed; that's fine because
            // items-end aligns by the dropdown's bottom edge anyway.
            gridTemplateColumns: 'minmax(0, 1fr) 4rem 2rem auto',
            gap: '0.4rem',
          }}
        >
          {/* Country */}
          <div style={{ minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontSize: '0.86rem',
                fontWeight: 600,
                color: 'rgba(26,26,46,0.6)',
                lineHeight: 1.1,
                marginBottom: '0.15rem',
              }}
            >
              🌍 Country
            </span>
            <select
              value={settings.country}
              title={countries[settings.country]?.name || ''}
              onChange={(e) => {
                const newCountry = e.target.value;
                const newCountryCcy = countries[newCountry]?.code;
                updS({
                  country: newCountry,
                  ...(newCountryCcy ? { displayCurrency: newCountryCcy } : {}),
                });
              }}
              style={{
                width: '100%',
                padding: '0.28rem 1.4rem 0.28rem 0.5rem',
                backgroundColor: '#fefefe',
                border: '1.5px solid #1a1a2e',
                borderRadius: '0.5rem',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                lineHeight: 1.1,
                outline: 'none',
                minWidth: 0,
              }}
            >
              {REGION_ORDER.map(region => {
                const entries = Object.entries(countries).filter(([, x]) => x.region === region);
                if (entries.length === 0) return null;
                return (
                  <optgroup key={region} label={REGION_LABELS[region]}>
                    {entries.map(([c, x]) => (
                      <option key={c} value={c}>
                        {c} · {x.name} ({x.symbol}){x.verified ? '' : ' *'}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </div>

          {/* Currency — the currency the calculator displays results in.
              Independent of the country setting (e.g. a Spanish seller can
              choose to view results in USD). Tooltip explains why. */}
          <div style={{ minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontSize: '0.86rem',
                fontWeight: 600,
                color: 'rgba(26,26,46,0.6)',
                lineHeight: 1.1,
                marginBottom: '0.15rem',
              }}
              title="Pick the currency you want results shown in. Independent of country — useful if you sell from one country but think in another currency."
            >
              💱 Currency
            </span>
            <select
              value={settings.displayCurrency}
              onChange={(e) => updS({ displayCurrency: e.target.value })}
              title={CURRENCY_INFO[settings.displayCurrency]?.name || ''}
              style={{
                width: '100%',
                padding: '0.28rem 1.2rem 0.28rem 0.4rem',
                backgroundColor: '#fefefe',
                border: '1.5px solid #1a1a2e',
                borderRadius: '0.5rem',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '0.93rem',
                lineHeight: 1.1,
                outline: 'none',
              }}
            >
              {(() => {
                const countryCcy = countries[settings.country]?.code;
                const popular = [...POPULAR_DISPLAY_CURRENCIES];
                if (countryCcy && !popular.includes(countryCcy) && CURRENCY_INFO[countryCcy]) {
                  popular.unshift(countryCcy);
                }
                return popular.map(code => {
                  const info = CURRENCY_INFO[code];
                  if (!info) return null;
                  return <option key={code} value={code}>{code}</option>;
                });
              })()}
            </select>
          </div>

          {/* Refresh */}
          <div>
            <span style={{ display: 'block', fontSize: '0.86rem', visibility: 'hidden', lineHeight: 1.1, marginBottom: '0.15rem' }}>·</span>
            <button
              type="button"
              onClick={onRefreshRates}
              disabled={ratesStatus?.fetching}
              title={ratesStatus?.fetching ? 'Fetching latest rates…' : 'Refresh exchange rates'}
              aria-label="Refresh exchange rates"
              style={{
                width: '2rem',
                height: '1.85rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.88rem',
                lineHeight: 1,
                cursor: ratesStatus?.fetching ? 'not-allowed' : 'pointer',
                border: '1.5px solid #1a1a2e',
                borderRadius: '0.4rem',
                backgroundColor: '#fefefe',
                boxShadow: '1px 2px 0 rgba(26,26,46,0.18)',
                opacity: ratesStatus?.fetching ? 0.5 : 1,
                padding: 0,
              }}
            >
              {ratesStatus?.fetching ? '⏳' : '🔃'}
            </button>
          </div>

          {/* Offsite Ads — segmented toggle */}
          <div>
            <span
              style={{
                display: 'block',
                fontSize: '0.86rem',
                fontWeight: 600,
                color: 'rgba(26,26,46,0.6)',
                lineHeight: 1.1,
                marginBottom: '0.15rem',
              }}
            >
              📡 Offsite Ads{' '}
              <Tooltip>Etsy ad attribution; 12–15% of order, capped at $100. Mandatory above $10K/yr.</Tooltip>
            </span>
            <div
              style={{
                display: 'inline-flex',
                border: '1.5px solid #1a1a2e',
                borderRadius: '0.4rem',
                overflow: 'hidden',
                boxShadow: '1px 2px 0 rgba(26,26,46,0.18)',
              }}
            >
              <button
                type="button"
                onClick={() => updS({ offsiteEnabled: false })}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.93rem',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  cursor: 'pointer',
                  border: 'none',
                  borderRight: '1.5px solid #1a1a2e',
                  backgroundColor: !settings.offsiteEnabled ? '#1e3a8a' : '#fefefe',
                  color: !settings.offsiteEnabled ? '#fefefe' : '#1e293b',
                  whiteSpace: 'nowrap',
                }}
              >
                Off
              </button>
              <button
                type="button"
                onClick={() => updS({ offsiteEnabled: true })}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.93rem',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: settings.offsiteEnabled ? '#1e3a8a' : '#fefefe',
                  color: settings.offsiteEnabled ? '#fefefe' : '#1e293b',
                  whiteSpace: 'nowrap',
                }}
              >
                On · {(settings.offsiteRate * 100).toFixed(0)}%
              </button>
            </div>
          </div>
        </div>

        {/* Unverified-country disclaimer — shown only when the picked
            country's fees are best-estimates. Helps users in those
            countries know to sanity-check against an actual payout. */}
        {countries[settings.country] && !countries[settings.country].verified && (
          <div
            role="note"
            style={{
              marginTop: '0.55rem',
              padding: '0.45rem 0.65rem',
              background: 'rgba(180, 83, 9, 0.08)',
              border: '1px solid rgba(180, 83, 9, 0.35)',
              borderRadius: '0.45rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.45rem',
            }}
          >
            <span style={{ fontSize: '0.95rem', flexShrink: 0, lineHeight: 1.2 }} aria-hidden="true">
              ⚠️
            </span>
            <p
              className="font-body"
              style={{
                fontSize: '0.78rem',
                color: '#7c2d12',
                lineHeight: 1.4,
                margin: 0,
              }}
            >
              <strong>{countries[settings.country].name}</strong> fees are{' '}
              <em>best-estimates</em> based on regional patterns — Etsy doesn't publish
              every country's exact rate. Run one real sale and compare your payout
              to the calculator's number; if they differ, the math here may be off
              by a few percent.
            </p>
          </div>
        )}

        {/* Status caption */}
        {(() => {
          const rs = ratesStatus || {};
          const lf = rs.lastFetched;
          const unverified = countries[settings.country] && !countries[settings.country].verified;
          let when = '';
          if (lf) {
            const mins = Math.round((Date.now() - lf) / 60000);
            const hrs  = Math.round((Date.now() - lf) / 3600000);
            const days = Math.round((Date.now() - lf) / 86400000);
            if (mins < 1) when = 'just now';
            else if (mins < 60) when = `${mins}m ago`;
            else if (hrs < 24)  when = `${hrs}h ago`;
            else                 when = `${days}d ago`;
          }
          const sourceDot = { live: '🟢', cache: '💾', fallback: '📌' }[rs.source] || '📌';
          const parts = [];
          if (rs.fetching) parts.push('fetching rates…');
          else if (rs.error) parts.push('offline, using cached rates');
          else if (lf) parts.push(`${sourceDot} ${when}`);
          if (unverified) parts.push('* country rate estimated');
          else parts.push('✓ verified Apr 2026');
          if (parts.length === 0) return null;
          return (
            <p
              className="font-note text-ink/72 mt-1 leading-tight"
              style={{ fontSize: '0.86rem' }}
            >
              ~ {parts.join(' · ')} ~
            </p>
          );
        })()}
      </div>

      {/* Number inputs */}
      <div className="space-y-3">
        {baseFields.map(({ name, emoji, label, tip }) => (
          <div key={name}>
            <Label>
              <span>{emoji} {label}</span>
              {tip && <Tooltip>{tip}</Tooltip>}
            </Label>
            <input
              type="number"
              name={name}
              value={inputs[name]}
              onChange={handleChange}
              className={`sketch-input ${errors[name] ? 'input-error' : ''}`}
              min="0" step="0.01" placeholder="0.00"
            />
            {errors[name] && (
              <p className="font-note text-note text-alert mt-1">{errors[name]}</p>
            )}
          </div>
        ))}

        {/* Discount + Quantity (row).
            Label + $/% toggle sit inline as one group with a small gap
            after "Discount". The number input on the right uses inline
            overrides to match the slim toggle height so the row reads as
            three peers instead of "tiny pills next to a giant input". */}
        <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
            <Label>🎁 Discount</Label>
            <div style={pillContainerStyle}>
              <button
                type="button"
                onClick={() => setInputs(p => ({ ...p, discountType: 'dollar' }))}
                style={pillStyle(inputs.discountType === 'dollar', true)}
              >$</button>
              <button
                type="button"
                onClick={() => setInputs(p => ({ ...p, discountType: 'percent' }))}
                style={pillStyle(inputs.discountType === 'percent', false)}
              >%</button>
            </div>
          </div>
          <input
            type="number" name="discount" value={inputs.discount} onChange={handleChange}
            className="sketch-input"
            style={{
              padding: '0.32rem 0.65rem',
              fontSize: '0.95rem',
              marginTop: 0,
              borderWidth: '1.5px',
              borderRadius: '0.5rem',
            }}
            min="0" step="0.01" placeholder="0"
          />
        </div>

        <div>
          <Label>🔢 Quantity</Label>
          <input
            type="number" name="quantity" value={inputs.quantity} onChange={handleChange}
            className={`sketch-input ${errors.quantity ? 'input-error' : ''}`}
            min="1"
          />
          {errors.quantity && (
            <p className="font-note text-note text-alert mt-1">{errors.quantity}</p>
          )}
        </div>

        {/* Hidden Costs */}
        <div>
          <button
            type="button"
            onClick={() => setHidden(!hidden)}
            className="flex items-center gap-1 font-title text-xl text-accent hover:text-primary transition-colors"
          >
            {hidden ? <ChevronDownIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
            Hidden Costs
            <span className="font-note text-note text-ink/68 ml-1">(often forgotten)</span>
          </button>

          {hidden && (
            <div
              className="mt-3 pl-3 space-y-3"
              style={{ borderLeft: '2px dashed rgba(30,41,59,0.3)' }}
            >
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>⏱️ Hours</Label>
                  <input type="number" name="laborHours" value={inputs.laborHours} onChange={handleChange}
                    className="sketch-input" min="0" step="0.25" placeholder="0" />
                </div>
                <div>
                  <Label>💵 Hourly $</Label>
                  <input type="number" name="laborRate" value={inputs.laborRate} onChange={handleChange}
                    className="sketch-input" min="0" step="0.50" placeholder="0" />
                </div>
              </div>
              <div>
                <Label>📮 Packaging</Label>
                <input type="number" name="packaging" value={inputs.packaging} onChange={handleChange}
                  className="sketch-input" min="0" step="0.01" placeholder="0" />
              </div>
              <div>
                <Label>🔄 Return %</Label>
                <input type="number" name="returnRate" value={inputs.returnRate} onChange={handleChange}
                  className="sketch-input" min="0" max="100" step="0.5" placeholder="0" />
              </div>
              <div className="col-span-2">
                <Label>
                  🏦 Tax set-aside{' '}
                  <Tooltip>
                    Adjusts displayed Net Profit to show what you actually keep after self-employment / income taxes. Most US makers set aside 25–30%.
                  </Tooltip>
                </Label>
                <select
                  value={settings.taxRate || 0}
                  onChange={(e) => updS({ taxRate: parseFloat(e.target.value) || 0 })}
                  className="sketch-input"
                >
                  <option value="0">No tax set-aside (off)</option>
                  <option value="15">15% — light freelance</option>
                  <option value="25">25% — typical US maker</option>
                  <option value="30">30% — higher self-employment</option>
                  <option value="35">35% — high earner / high state tax</option>
                  <option value="40">40% — aggressive set-aside</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label>
                  🏷️ Product category{' '}
                  <Tooltip>
                    Optional. Tells the calculator which industry margin range to compare you against. Just for context — doesn't affect any math.
                  </Tooltip>
                </Label>
                <select
                  value={settings.productCategory || 'none'}
                  onChange={(e) => updS({ productCategory: e.target.value })}
                  className="sketch-input"
                >
                  <option value="none">— pick a category —</option>
                  {CATEGORY_ORDER.map(key => (
                    <option key={key} value={key}>
                      {CATEGORY_BENCHMARKS[key].label}{' '}
                      (median {CATEGORY_BENCHMARKS[key].median}%)
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* CSS Calculate button (no image) */}
        <button onClick={onCalculate} className="calc-btn">
          🧮  Calculate
        </button>
      </div>
    </div>
  );
}
